// ═══════════════════════════════════════════════════════════════════
// PlazaFolkStage.js — THE FOLK REVIEW ROOM   ·   ?stage=folk&who=vael
// ═══════════════════════════════════════════════════════════════════
// caP'n, 2026-09-16: "try your absolute hardest to make her look exactly
// like the V'ael in this picture … let's get this looking bar for bar
// like the pics."
//
// Bar for bar needs the bar on screen. This room puts the REAL art on
// the left (straight off terror-assets, the same file the game ships)
// and the 3D body on the right, under the Plaza's own light rig, with a
// free orbit — so a comparison is a screenshot, not a memory. No game
// state, no Firebase, no Plaza. Routed in index.js beside ?stage=bodies.
//
//   ?stage=folk               → V'ael
//   ?stage=folk&who=lyraei    → Lyraei (the Aenarchist, f)
//   ?stage=folk&who=<any id>  → any body plazaFolk3D knows
//   &turn=1                   → slow turntable
// ═══════════════════════════════════════════════════════════════════
import { useMemo, useRef, useState } from "react";
import { Canvas, useFrame } from "@react-three/fiber";
import { OrbitControls } from "@react-three/drei";
import * as THREE from "three";
import { CDN } from "./engineIndex";
import { FolkBody, FOLK_REFERENCE, FOLK_CROP, folkHeight } from "./plazaFolk3D";
import { LIGHT, G } from "./plazaPieces3D";

const q = (k, d = null) => { try { return new URLSearchParams(window.location.search).get(k) ?? d; } catch (_) { return d; } };

function Stand({ id, turn }) {
  const g = useRef();
  const ctl = useRef({ facing: { x: 0, y: 1 }, speed: 0 }).current;
  ctl.speed = q("walk", "") ? 1 : 0;   // ?walk=1 — review the walk cycle in place
  useFrame((_, dt) => { if (turn && g.current) g.current.rotation.y += dt * 0.35; });
  const h = folkHeight(id);
  return (
    <group ref={g}>
      {/* the pedestal — a plaza cobble disc */}
      <mesh position={[0, -0.05, 0]} receiveShadow><cylinderGeometry args={[h * 0.42, h * 0.46, 0.1, 24]} /><meshStandardMaterial color="#2a2236" roughness={0.9} /></mesh>
      <mesh position={[0, 0.002, 0]} rotation={[-Math.PI / 2, 0, 0]}><ringGeometry args={[h * 0.36, h * 0.4, 32]} /><meshBasicMaterial color="#B388FF" transparent opacity={0.25} toneMapped={false} /></mesh>
      <FolkBody id={id} ctl={ctl} />
    </group>
  );
}

export default function PlazaFolkStage() {
  const id = q("who", "vael");
  const turn = q("turn") === "1";
  const [side, setSide] = useState(true);
  const ref = FOLK_REFERENCE[id];
  const crop = FOLK_CROP[id];
  const h = folkHeight(id);
  // &cam=az,el,dist,ty — an initial framing by URL, so a review shot is a
  // link and not a drag (the automated reviewer cannot drag).
  const framing = useMemo(() => {
    const c = (q("cam") || "").split(",").map(Number);
    const az = isFinite(c[0]) ? c[0] : 0, el = isFinite(c[1]) ? c[1] : 0.12, dist = isFinite(c[2]) ? c[2] : h * 2.6, ty = isFinite(c[3]) ? c[3] : h * 0.5;
    return { pos: [Math.sin(az) * Math.cos(el) * dist, ty + Math.sin(el) * dist, Math.cos(az) * Math.cos(el) * dist], target: [0, ty, 0] };
  }, [h]);
  const cam = useMemo(() => ({ fov: 30, near: 0.1, far: 100, position: framing.pos }), [framing]);
  return (
    <div style={{ position: "fixed", inset: 0, background: "#07040f", display: "flex", fontFamily: "Georgia, serif", color: "#e8dcc8" }}>
      {side && (
        <div style={{ width: "34%", borderRight: "1px solid #3a2a4a", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", background: "#0d0916", position: "relative" }}>
          {ref && crop ? (
            // a painted-in person: show just their corner of the painting
            <div style={{ width: "92%", aspectRatio: `${crop[2]} / ${crop[3]}`, maxHeight: "88vh",
              backgroundImage: `url(${CDN}/${ref})`, backgroundRepeat: "no-repeat",
              backgroundSize: `${100 / crop[2]}% ${100 / crop[3]}%`,
              backgroundPosition: `${(crop[0] / (1 - crop[2])) * 100}% ${(crop[1] / (1 - crop[3])) * 100}%`,
              borderRadius: 8, boxShadow: "0 8px 24px rgba(0,0,0,.8)" }} />
          ) : ref ? <img src={`${CDN}/${ref}`} alt={id} style={{ maxHeight: "92vh", maxWidth: "94%", objectFit: "contain", filter: "drop-shadow(0 8px 24px rgba(0,0,0,.8))" }} />
               : <div style={{ opacity: 0.5 }}>no reference on the CDN for “{id}”</div>}
          <div style={{ position: "absolute", top: 10, left: 12, fontSize: 11, letterSpacing: 2, opacity: 0.6 }}>THE PICTURE · {ref}</div>
        </div>
      )}
      <div style={{ flex: 1, position: "relative" }}>
        <Canvas shadows dpr={[1, 1.5]} camera={cam}
          gl={{ antialias: true, toneMapping: THREE.ACESFilmicToneMapping, toneMappingExposure: G(LIGHT.exposure) }}
          onCreated={({ gl, scene, camera }) => {
            gl.setClearColor("#07040f");
            // dev handles for the review loop. A backgrounded tab pauses rAF,
            // so `__folkShot(azimuth, elevation, dist)` places the lens and
            // paints ONE frame by hand — the automated reviewer's only eye.
            window.__folkScene = scene; window.__folkCam = camera; window.__folkGL = gl;
            window.__folkShot = (az = 0, el = 0.15, dist = h * 2.6, ty = h * 0.5) => {
              camera.position.set(Math.sin(az) * Math.cos(el) * dist, ty + Math.sin(el) * dist, Math.cos(az) * Math.cos(el) * dist);
              camera.lookAt(0, ty, 0); camera.updateProjectionMatrix();
              scene.traverse((o) => { if (o.material && o.material.map && o.material.map.image) o.material.map.needsUpdate = true; });
              gl.render(scene, camera); return "shot";
            };
          }}>
          <ambientLight intensity={G(LIGHT.ambient)} color="#b8a8e0" />
          <hemisphereLight args={["#6a4aa0", "#1a0f2a", G(LIGHT.hemi)]} />
          <directionalLight position={[-3, 6, 5]} intensity={G(LIGHT.key)} color="#d8c8ff" castShadow shadow-mapSize-width={1024} shadow-mapSize-height={1024} />
          <directionalLight position={[3, 3, -3]} intensity={0.5} color="#8a7cff" />
          <pointLight position={[2, 2.2, 2.5]} intensity={1.6} distance={9} decay={1.4} color="#a070ff" />
          <Stand id={id} turn={turn} />
          <OrbitControls target={framing.target} enablePan={false} minDistance={0.3} maxDistance={8} maxPolarAngle={Math.PI / 1.85} />
        </Canvas>
        <div style={{ position: "absolute", top: 10, left: 12, fontSize: 11, letterSpacing: 2, opacity: 0.6 }}>THE BODY · {id} · drag to orbit · scroll to zoom</div>
        <button onClick={() => setSide((v) => !v)} style={{ position: "absolute", top: 8, right: 12, background: "transparent", color: "#c9a84c", border: "1px solid #c9a84c66", borderRadius: 4, padding: "3px 8px", fontSize: 10, letterSpacing: 1.5, cursor: "pointer" }}>{side ? "HIDE PICTURE" : "SHOW PICTURE"}</button>
      </div>
    </div>
  );
}
